import { useMemo, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, Clock, X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { Reveal, Rise } from "./Reveal";
import content from "../content";

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const today = new Date();
const isCurrent = (m) => m.year === today.getFullYear() && m.month === today.getMonth() + 1;
const defaultDay = (m) =>
  isCurrent(m) ? today.getDate() : m.events?.[0]?.day ?? m.events?.[0]?.days?.[0] ?? 1;

export default function MonthCalendar() {
  const { months = [], weekly = [] } = content.esteMes.calendar;
  const [mIdx, setMIdx] = useState(() => Math.max(0, months.findIndex(isCurrent)));
  const { year, month, events = [], monthLabel } = months[mIdx];
  const monthWord = monthLabel.split(" ")[0];

  const eventsForDay = (day) => {
    if (!day) return [];
    const wd = new Date(year, month - 1, day).getDay();
    const specific = events.filter(
      (e) => e.day === day || (Array.isArray(e.days) && e.days.includes(day))
    );
    const recurring = specific.some((e) => e.replacesWeekly) ? [] : weekly.filter((w) => w.weekday === wd);
    return [...specific, ...recurring];
  };

  const [selected, setSelected] = useState(() => defaultDay(months[mIdx]));
  const changeMonth = (dir) => {
    const next = mIdx + dir;
    if (next < 0 || next >= months.length) return;
    setMIdx(next);
    setSelected(defaultDay(months[next]));
  };

  const cells = useMemo(() => {
    const firstWeekday = (new Date(year, month - 1, 1).getDay() + 6) % 7; // 0 = lunes
    const daysInMonth = new Date(year, month, 0).getDate();
    const arr = Array.from({ length: firstWeekday }, () => null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(d);
    return arr;
  }, [year, month]);

  const isToday = (d) =>
    d && today.getFullYear() === year && today.getMonth() === month - 1 && today.getDate() === d;

  const selectedEvents = eventsForDay(selected);
  const imageEvents = selectedEvents.filter((e) => e.image);

  const [lightbox, setLightbox] = useState(null);
  const closeLightbox = useCallback(() => setLightbox(null), []);
  const goLightbox = useCallback(
    (dir) =>
      setLightbox((i) =>
        i === null ? i : (i + dir + imageEvents.length) % imageEvents.length
      ),
    [imageEvents.length]
  );

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") goLightbox(1);
      if (e.key === "ArrowLeft") goLightbox(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, closeLightbox, goLightbox]);

  return (
    <Reveal className="mt-16 md:mt-20">
      <div className="w-full max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-6 md:gap-8 items-start">
        {/* Cuadrícula del mes */}
        <Rise>
          <div className="glass rounded-[1.75rem] p-6 md:p-8" data-testid="month-calendar">
            <div className="flex items-center gap-3 mb-6">
              <CalendarDays size={20} className="text-electric" />
              <h3 className="display text-white text-2xl md:text-3xl" data-testid="calendar-month-label">
                {monthLabel}
              </h3>
              {months.length > 1 && (
                <div className="ml-auto flex gap-2">
                  {[-1, 1].map((dir) => {
                    const disabled = mIdx + dir < 0 || mIdx + dir >= months.length;
                    const Icon = dir < 0 ? ChevronLeft : ChevronRight;
                    return (
                      <button
                        key={dir}
                        type="button"
                        onClick={() => changeMonth(dir)}
                        disabled={disabled}
                        aria-label={dir < 0 ? "Mes anterior" : "Mes siguiente"}
                        data-testid={dir < 0 ? "calendar-prev-month" : "calendar-next-month"}
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-white transition-colors duration-200 hover:bg-white/10 disabled:opacity-30 disabled:pointer-events-none cursor-pointer"
                      >
                        <Icon size={18} />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <motion.div
              key={mIdx}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="grid grid-cols-7 gap-1.5 md:gap-2"
            >
              {WEEKDAYS.map((w) => (
                <div
                  key={w}
                  className="text-center font-poppins text-[10px] md:text-xs tracking-widest uppercase text-white/40 pb-2"
                >
                  {w}
                </div>
              ))}
              {cells.map((d, i) => {
                if (!d) return <div key={`empty-${i}`} aria-hidden="true" />;
                const has = eventsForDay(d).length > 0;
                const isSel = selected === d;
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setSelected(d)}
                    aria-label={`${d} de ${monthWord}`}
                    aria-pressed={isSel}
                    data-testid={`calendar-day-${d}`}
                    className={`relative aspect-square rounded-xl flex items-center justify-center font-poppins text-sm md:text-base transition-colors duration-200 cursor-pointer ${
                      isSel
                        ? "bg-electric text-white shadow-[0_0_20px_rgba(33,112,232,0.6)]"
                        : has
                        ? "bg-white/5 text-white hover:bg-white/10"
                        : "text-white/45 hover:bg-white/5"
                    }`}
                  >
                    {d}
                    {has && !isSel && (
                      <span className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-electric" />
                    )}
                    {isToday(d) && (
                      <span className="absolute inset-0 rounded-xl ring-1 ring-inset ring-gold/70 pointer-events-none" />
                    )}
                  </button>
                );
              })}
            </motion.div>

            <div className="mt-5 flex items-center gap-5 font-poppins text-[11px] text-white/45">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-electric" /> Con evento
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-3 h-3 rounded ring-1 ring-gold/70" /> Hoy
              </span>
            </div>
          </div>
        </Rise>

        {/* Panel de detalle del día */}
        <Rise>
          <div className="glass rounded-[1.75rem] p-6 md:p-8 min-h-[240px]" data-testid="calendar-detail">
            <p className="eyebrow mb-5">
              {selected} de {monthWord}
            </p>
            {selectedEvents.length === 0 ? (
              <p className="font-poppins text-white/50 text-sm">
                No hay eventos programados este día.
              </p>
            ) : (
              <ul className="space-y-5">
                {selectedEvents.map((ev, i) => (
                  <li key={`${ev.title}-${i}`} className="flex gap-4" data-testid={`calendar-event-${i}`}>
                    {ev.image && (
                      <button
                        type="button"
                        onClick={() => setLightbox(imageEvents.findIndex((x) => x === ev))}
                        aria-label={`Ampliar ${ev.title}`}
                        data-testid={`calendar-event-image-${i}`}
                        className="group relative w-16 shrink-0 overflow-hidden rounded-lg border border-white/10 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-electric"
                      >
                        <img
                          src={ev.image}
                          alt={ev.title}
                          loading="lazy"
                          className="w-full object-cover aspect-[4/5] transition-transform duration-500 group-hover:scale-105"
                        />
                        <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/40 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <ZoomIn size={16} />
                        </span>
                      </button>
                    )}
                    <div>
                      <h4 className="font-poppins font-semibold text-white text-base leading-snug mb-1.5">
                        {ev.title}
                      </h4>
                      {ev.time && (
                        <p className="inline-flex items-center gap-2 font-poppins text-electric text-sm">
                          <Clock size={14} /> {ev.time}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Rise>
      </div>

      <AnimatePresence>
        {lightbox !== null && imageEvents[lightbox] && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
            data-testid="calendar-lightbox"
          >
            <button
              onClick={closeLightbox}
              aria-label="Cerrar"
              data-testid="calendar-lightbox-close"
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
            >
              <X size={22} />
            </button>
            {imageEvents.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); goLightbox(-1); }}
                  aria-label="Anterior"
                  data-testid="calendar-lightbox-prev"
                  className="absolute left-3 md:left-6 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <ChevronLeft size={26} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); goLightbox(1); }}
                  aria-label="Siguiente"
                  data-testid="calendar-lightbox-next"
                  className="absolute right-3 md:right-6 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <ChevronRight size={26} />
                </button>
              </>
            )}
            <motion.div
              key={imageEvents[lightbox].image}
              onClick={(e) => e.stopPropagation()}
              className="flex flex-col items-center"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 26 }}
            >
              <img
                src={imageEvents[lightbox].image}
                alt={imageEvents[lightbox].title}
                className="max-h-[80vh] max-w-[92vw] rounded-2xl shadow-2xl object-contain"
              />
              <p className="mt-4 text-center font-poppins text-white text-sm md:text-base">
                <span className="font-semibold">{imageEvents[lightbox].title}</span>
                {imageEvents[lightbox].time && (
                  <span className="block text-electric mt-1">{imageEvents[lightbox].time}</span>
                )}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Reveal>
  );
}
